import {
  buildViewerSlaHold,
  getApprovalSlaProgress,
} from "./approvalSlaHold";
import { APPROVAL_SLA_REQUEST_TYPES } from "./requestTransparency.constants";

export const APPROVER_ROLE_LABELS = {
  manager: "المدير المباشر",
  team_leader: "قائد الفريق",
  supervisor: "المشرف",
  branch_manager: "مدير الفرع",
  regional_manager: "المدير الإقليمي",
  skip_manager: "مدير المدير",
  department_manager: "مدير الإدارة",
  marketing_department_manager: "مدير إدارة التسويق",
  hr: "الموارد البشرية",
  hr_staff: "الموارد البشرية",
  gm: "المدير العام",
  accountant: "المحاسب",
  accountant_head: "رئيس الحسابات",
};

const OPEN_STATUSES = new Set([
  "pending",
  "gm_pending",
  "manager_approved",
  "PENDING",
  "IN_PROGRESS",
]);

const REJECTED_STATUSES = new Set([
  "rejected",
  "manager_rejected",
  "dm_rejected",
  "REJECTED",
]);

const LEAVE_CODE_BY_LABEL = {
  "إجازة اعتيادية": "annual",
  "إجازة سنوية": "annual",
  "إجازة مرضية": "sick",
  "إجازة عارضة": "casual",
  "إجازة على الحساب الخاص": "unpaid",
  "إجازة بدون راتب": "unpaid",
  "إجازة تعويضية": "compensatory",
  "إجازة ممنوحة": "special",
};

function asDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function roleDisplayName(slug, fallbackName) {
  if (slug && APPROVER_ROLE_LABELS[slug]) return APPROVER_ROLE_LABELS[slug];
  return String(fallbackName || slug || "المعتمد").trim();
}

export function formatRemainingLabel(remainingMs, paused) {
  if (paused && remainingMs > 0) {
    return "متوقف مؤقتًا أثناء الراحة الأسبوعية أو الإجازة";
  }
  if (remainingMs <= 0) return "حان موعد الرد";
  const totalMin = Math.max(1, Math.round(remainingMs / 60000));
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    if (remHours === 0) {
      return days === 1 ? "باقي يوم واحد" : `باقي ${days} أيام`;
    }
    return `باقي ${days}ي و ${remHours}س`;
  }
  if (hours === 0) return `باقي ${minutes} دقيقة`;
  if (minutes === 0) {
    return hours === 1 ? "باقي ساعة واحدة" : `باقي ${hours} ساعات`;
  }
  return `باقي ${hours}س و ${minutes}د`;
}

export function formatCountdownClock(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}ي ${pad(hours % 24)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function formatTransparencyDate(value) {
  const date = asDate(value);
  if (!date) return "";
  return date.toLocaleString("ar-EG", {
    timeZone: "Africa/Cairo",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function workflowType(request) {
  return (
    request?.workflow?.requestType ||
    request?.request?.type ||
    request?.type ||
    request?._source ||
    ""
  );
}

function slaApplies(request, isOpen) {
  if (!isOpen) return false;
  const type = String(workflowType(request)).toLowerCase();
  return APPROVAL_SLA_REQUEST_TYPES.some(
    (item) => type === item || type.startsWith(`${item}_`),
  );
}

function currentApproverSlug(request) {
  const steps = request?.workflow?.steps || request?.request?.workflow?.steps || [];
  const current = request?.currentStep ?? request?.request?.currentStep ?? null;
  const step = steps.find((item) => Number(item.stepOrder) === Number(current));
  return step?.approverRole?.slug || request?.transparency?.currentHolder?.roleSlug || null;
}

export function getRequestSlaHours(request) {
  const slug = currentApproverSlug(request);
  if (slug === "gm" || request?.status === "gm_pending") return 72;
  return 24;
}

function performerName(action) {
  return (
    action?.performedBy?.employee?.fullName ||
    action?.performedBy?.username ||
    action?.actorName ||
    null
  );
}

function rejectionFromActions(actions = []) {
  const reject = [...actions]
    .reverse()
    .find((action) => action?.action === "REJECTED" || action?.isReject);
  if (!reject) return null;
  return {
    reason: reject.notes || reject.reason || "تم رفض الطلب",
    byName: performerName(reject),
    at: reject.createdAt || reject.at || null,
  };
}

function fallbackDisbursement(request) {
  const status = request?.disbursementStatus;
  if (!status) return null;
  const assigneeName =
    request.disbursementAssigneeLabel ||
    request.disbursementAssignee?.fullName ||
    null;
  if (status === "DISBURSED") {
    return {
      status,
      expectedAt: request.disbursementConfirmedAt || null,
      expectedLabel: "تم تأكيد الصرف",
      assigneeName,
    };
  }
  if (status === "ASSIGNED") {
    const postponed = request.disbursementPostponedUntil
      ? String(request.disbursementPostponedUntil).slice(0, 10)
      : null;
    return {
      status,
      expectedAt: postponed,
      expectedLabel: postponed
        ? `مؤجل حتى ${postponed}`
        : "تم توجيه الصرف — متوقع خلال يوم عمل",
      assigneeName,
    };
  }
  if (status === "PENDING_ASSIGNMENT") {
    return {
      status,
      expectedAt: null,
      expectedLabel:
        "بانتظار توجيه المحاسب — الصرف عادة خلال يومي عمل بعد التوجيه",
      assigneeName,
    };
  }
  return null;
}

export function resolveRequestTransparency(request, now = new Date()) {
  if (request?.transparency?.timeline?.length) {
    const transparency = request.transparency;
    if (transparency.sla?.applies) {
      const start =
        asDate(request.stepEnteredAt) ||
        asDate(request.currentStepEnteredAt) ||
        asDate(request.submittedAt);
      const slaHours = transparency.sla.hours || getRequestSlaHours(request);
      if (start) {
        const progress = getApprovalSlaProgress(
          start,
          slaHours,
          buildViewerSlaHold({ weeklyRestDay: "FRIDAY" }),
          now,
        );
        return {
          ...transparency,
          disbursement:
            transparency.disbursement || fallbackDisbursement(request),
          sla: {
            ...transparency.sla,
            remainingMs: progress.remainingMs,
            paused: progress.paused,
            deadline: progress.deadline?.toISOString?.() || transparency.sla.deadline,
            label: formatRemainingLabel(progress.remainingMs, progress.paused),
          },
        };
      }
    }
    return {
      ...transparency,
      disbursement: transparency.disbursement || fallbackDisbursement(request),
    };
  }

  const status = request?.status || "pending";
  const isOpen = OPEN_STATUSES.has(status);
  const isRejected = REJECTED_STATUSES.has(status);
  const steps = [
    ...(request?.workflow?.steps || request?.request?.workflow?.steps || []),
  ].sort((a, b) => Number(a.stepOrder) - Number(b.stepOrder));
  const actions = request?.actions || request?.request?.actions || [];
  const currentStep = Number(
    request?.currentStep ?? request?.request?.currentStep ?? steps[0]?.stepOrder ?? 1,
  );
  const current = steps.find((step) => Number(step.stepOrder) === currentStep);
  const currentHolder =
    isOpen && current
      ? {
          roleName: roleDisplayName(
            current.approverRole?.slug,
            current.approverRole?.name,
          ),
          roleSlug: current.approverRole?.slug || "",
        }
      : request?.nextApprover
        ? {
            roleName: roleDisplayName(
              request.nextApprover.roleSlug || request.nextApprover.approverRole?.slug,
              request.nextApprover.roleName || request.nextApprover.approverRole?.name,
            ),
            roleSlug:
              request.nextApprover.roleSlug ||
              request.nextApprover.approverRole?.slug ||
              "",
          }
        : null;

  const timeline = [
    {
      key: "submitted",
      label: "قُدّم الطلب",
      status: "done",
      at: request?.createdAt || request?.submittedAt || null,
      actorName: null,
      notes: null,
    },
  ];

  steps.forEach((step) => {
    const stepOrder = Number(step.stepOrder);
    const stepActions = actions.filter(
      (action) => Number(action.stepOrder) === stepOrder,
    );
    const reject = [...stepActions]
      .reverse()
      .find((action) => action.action === "REJECTED");
    const approve = [...stepActions]
      .reverse()
      .find(
        (action) => action.action === "APPROVED" || action.action === "GM_OVERRIDE",
      );
    const skip = [...stepActions]
      .reverse()
      .find((action) => String(action.notes || "").startsWith("AUTO_APPROVAL_SKIP:"));

    let stepStatus = "upcoming";
    if (skip) stepStatus = "skipped";
    else if (reject || (isRejected && stepOrder === currentStep)) stepStatus = "rejected";
    else if (approve || (!isOpen && !isRejected && stepOrder <= currentStep)) {
      stepStatus = "done";
    } else if (isOpen && stepOrder === currentStep) stepStatus = "current";
    else if (stepOrder < currentStep) stepStatus = "done";

    const outcome = reject || skip || approve;
    timeline.push({
      key: `step-${stepOrder}`,
      label: roleDisplayName(step.approverRole?.slug, step.approverRole?.name),
      status: stepStatus,
      at: outcome?.createdAt || (stepStatus === "current" ? request?.stepEnteredAt : null),
      actorName: performerName(outcome),
      notes: skip
        ? "تم التخطي لتأخر الرد أكثر من المهلة المحددة"
        : outcome?.notes || null,
    });
  });

  const slaHours = getRequestSlaHours(request);
  const start =
    asDate(request?.stepEnteredAt) ||
    asDate(request?.currentStepEnteredAt) ||
    asDate(request?.submittedAt);
  const applies = slaApplies(request, isOpen) && Boolean(start);
  const progress = applies
    ? getApprovalSlaProgress(
        start,
        slaHours,
        buildViewerSlaHold({ weeklyRestDay: "FRIDAY" }),
        now,
      )
    : null;

  const rejection =
    request?.transparency?.rejection ||
    rejectionFromActions(actions) ||
    (isRejected && request?.rejectionReason
      ? { reason: request.rejectionReason, byName: null, at: null }
      : isRejected
        ? { reason: "تم رفض الطلب", byName: null, at: null }
        : null);

  return {
    currentHolder,
    sla: applies
      ? {
          applies: true,
          hours: slaHours,
          deadline: progress.deadline?.toISOString?.() || null,
          remainingMs: progress.remainingMs,
          paused: progress.paused,
          label: formatRemainingLabel(progress.remainingMs, progress.paused),
        }
      : null,
    timeline,
    rejection,
    disbursement:
      request?.transparency?.disbursement || fallbackDisbursement(request),
    canResubmit: isRejected,
  };
}

export function buildResubmitDraft(request) {
  if (!request) return null;
  const source = request._source || request.category;
  if (source === "permit" || request.category === "permit" || request.type === "إذن" || request.type === "إذن تعويضي") {
    return {
      tab: "leave",
      kind: "permit",
      leaveType: request.isCompensatory ? "CompensatoryPermit" : "Permit",
      startDate: String(request.startDate || request.permitDate || "").slice(0, 10),
      permitStartTime: request.permitStartTime || request.startTime || "",
      permitEndTime: request.permitEndTime || request.endTime || "",
      reason: request.reason || "",
    };
  }
  if (source === "leave" || String(request.type || "").includes("إجازة")) {
    const code =
      request.leaveTypeCode ||
      LEAVE_CODE_BY_LABEL[request.type] ||
      String(request.leaveType || "").toLowerCase();
    return {
      tab: "leave",
      kind: "leave",
      leaveType: code || "annual",
      startDate: String(request.startDate || "").slice(0, 10),
      days: String(request.days ?? ""),
      halfDayPeriod: request.halfDayPeriod || "",
      reason: request.reason || "",
    };
  }
  if (source === "advance" || request.type === "سلفة") {
    return {
      tab: "advance",
      kind: "advance",
      amount: String(request.amount ?? ""),
      installments: String(request.installments ?? "1"),
      guarantorName: request.guarantorName || "",
      reason: request.reasonText || request.reason || "",
    };
  }
  if (source === "overtime" || request.type === "إضافي") {
    return {
      tab: "overtime",
      kind: "overtime",
      reason: request.reason || "",
      requestedSettlement: request.requestedSettlement || "",
    };
  }
  if (source === "wfh" || request.type === "عمل عن بُعد") {
    return {
      tab: "wfh",
      kind: "wfh",
      dateFrom: String(request.dateFrom || "").slice(0, 10),
      dateTo: String(request.dateTo || "").slice(0, 10),
      wfhType: request.wfhType || request.requestType || "DAILY",
      reason: request.reason || "",
    };
  }
  return null;
}

export function getResubmitNavigation(request) {
  const draft = buildResubmitDraft(request);
  if (!draft) return null;
  if (draft.kind === "overtime") {
    return { id: "overtime", options: { draft } };
  }
  if (draft.kind === "wfh") {
    return { id: "wfh", options: { tab: "wfh", draft } };
  }
  return { id: "leave", options: { tab: draft.tab, draft } };
}

export function attachTransparencyFields(mapped, raw) {
  const actions = raw?.actions || raw?.request?.actions || mapped.actions || [];
  const rejection = rejectionFromActions(actions);
  return {
    ...mapped,
    actions,
    transparency: raw?.transparency || mapped.transparency || null,
    rejectionReason:
      mapped.rejectionReason ||
      raw?.rejectionReason ||
      rejection?.reason ||
      null,
    leaveTypeCode:
      mapped.leaveTypeCode ||
      String(raw?.leaveType || "").toLowerCase() ||
      null,
  };
}
